import { Divider, Timeline } from "antd";
import dayjs from "dayjs";

import {
  type ProductClient,
  type ScheduledAccess,
  type SubscriptionProductAccessPeriod,
} from "@fitness-app/data-models/entities/ProductClient";
import { areInstallmentsSettled } from "@fitness-app/utils/src/products/areInstallmentsSettled";

/**
 * Przesunięcie o `periods` okresów z zachowaniem dnia kotwicy.
 *
 * `dayjs.add(1, "month")` przycina do długości miesiąca i już go nie oddaje:
 * 31.01 → 28.02 → 28.03, podczas gdy Stripe pamięta kotwicę i fakturuje 31.03.
 * Bez przywrócenia dnia oś czasu gubiła po dobie na każdym krótkim miesiącu.
 */
const shiftPeriods = (
  from: dayjs.Dayjs,
  periods: number,
  recurring: SubscriptionProductAccessPeriod["recurring"],
  anchorDay: number,
) => {
  // Bez przesunięcia nie ma czego odtwarzać — `from` JEST datą pobrania, choćby
  // wypadała poza rytmem kotwicy (koniec darmowego okresu, koniec trialu).
  if (periods === 0) {
    return from;
  }

  const shifted = from.add(periods * (recurring.interval_count || 1), recurring.interval);

  return recurring.interval === "month" || recurring.interval === "year"
    ? shifted.date(Math.min(anchorDay, shifted.daysInMonth()))
    : shifted;
};

/**
 * Dzień kotwicy rozliczeniowej odtworzony z granic bieżącego okresu.
 *
 * Punktem wyjścia jest koniec okresu — to dzień najbliższego pobrania. Może być
 * przycięty przez krótki miesiąc, a wtedy prawdziwą kotwicę zna początek okresu
 * (przy kotwicy na 31. para 31.01 → 28.02 daje z powrotem 31). Ale tylko wtedy,
 * gdy okres trwa dokładnie jeden interwał: w trialu przedłużenia jego początek
 * to dzień zakupu, a w okresie `add_free_days` — początek darmowych dni. Żaden
 * z nich nie ma nic wspólnego z rytmem kotwicy i podniósłby ją na zły dzień.
 */
const billingAnchorDay = (accessPeriod: SubscriptionProductAccessPeriod) => {
  const periodStart = dayjs.unix(accessPeriod.currentPeriodStart);
  const periodEnd = dayjs.unix(accessPeriod.currentPeriodEnd);
  const isRegularPeriod = periodStart
    .add(accessPeriod.recurring.interval_count || 1, accessPeriod.recurring.interval)
    .isSame(periodEnd, "day");

  return isRegularPeriod ? Math.max(periodStart.date(), periodEnd.date()) : periodEnd.date();
};

/** `cycleNumber` bywa puste na starych wierszach, a `undefined + 1` to NaN. */
const billedInstallments = (cycleNumber: number | null | undefined) =>
  Number.isFinite(cycleNumber) ? (cycleNumber as number) : 0;

/**
 * Dzień pobrania raty numer `installment` (licząc od 1).
 *
 * `accessPeriod.startAt` to moment ZAŁOŻENIA subskrypcji, a nie dzień pobrania
 * kolejnych rat — licząc `startAt + n okresów` admin mylił się wszędzie tam,
 * gdzie te dwie rzeczy się rozjeżdżają: po przesunięciu kotwicy (plan kupiony
 * 20.05, fakturowany 31.) i przy przedłużeniu z odroczonym startem, gdzie raty
 * szły co miesiąc od dnia zakupu i wchodziły w środek trwającego planu — na osi
 * rata 2/12 lądowała nad ratą 6/6 (TRE-2031).
 *
 * Kotwicą jest `currentPeriodEnd`: to dzień pobrania raty `cycleNumber + 1`.
 * Świadomie nie `currentPeriodStart`, choć wydaje się bliższy — ten drugi
 * kłamie w dwóch stanach:
 *   • w trialu przedłużenia (`currentPeriodStart` = dzień zakupu, a rata 2 idzie
 *     dopiero po końcu poprzedniego dostępu, czasem cztery miesiące później);
 *   • w okresie `add_free_days`, gdzie cykl przesuwa granice okresu, ale
 *     `cycleNumber` celowo zostawia w miejscu (TRE-1724) — anchor na starcie
 *     okresu podpisałby darmowy miesiąc jako ostatnią opłaconą ratę.
 * `currentPeriodEnd` w obu jest tym, czym ma być: dniem najbliższego pobrania.
 *
 * Rata 1 jest wyjątkiem, bo jako jedyna bywa pobrana poza rytmem kotwicy —
 * przy zakupie. Dopóki nie poszła (`cycleNumber` 0, okres rozruchowy) pobierze
 * się z końcem bieżącego okresu, czyli tam gdzie wskazuje wzór.
 */
export const installmentPaymentDate = (accessPeriod: SubscriptionProductAccessPeriod, installment: number) => {
  const billed = billedInstallments(accessPeriod.cycleNumber);

  if (installment === 1 && billed >= 1) {
    return dayjs.unix(accessPeriod.startAt);
  }

  return shiftPeriods(
    dayjs.unix(accessPeriod.currentPeriodEnd),
    installment - billed - 1,
    accessPeriod.recurring,
    billingAnchorDay(accessPeriod),
  );
};

interface ProductClientScheduledAccessProps {
  scheduledAccess: ScheduledAccess[];
  productClient: ProductClient;
}

export const ProductClientScheduledAccess = ({ scheduledAccess, productClient }: ProductClientScheduledAccessProps) => {
  if (productClient.accessPeriod?.type === "one_time") {
    const currentCycles: any[] = [];
    const scheduledAccessCycle = scheduledAccess
      .map((access, index) => {
        const cycleIndex = index;
        if ("type" in access && (access.type === "one_time" || access.type === "free_extension")) {
          return {
            id: index,
            startAt: dayjs.unix(access.start_date),
            start: true,
            installment: 1,
            totalInstallments: 1,
            paid: true,
            paidDate: access.metadata?.prepaidDate,
            type: access.type,
            end: dayjs.unix(access.end_date),
          };
        }
        if (access.type === "recurring") {
          const scheduledCycles = Array.from({
            length: access.installments ?? 1,
          }).map((_, index) => {
            // @ts-expect-error ignore type error
            const paidDueDate = (scheduledAccess[cycleIndex]?.metadata?.updatedPaymentDate?.[index] || null) as
              | string
              | null;
            return {
              startAt: installmentPaymentDate(access, index + 1),
              id: index,
              installment: index + 1,
              totalInstallments: access.installments,
              paid: areInstallmentsSettled(access)
                ? true
                : paidDueDate
                  ? dayjs(paidDueDate).isBefore(dayjs())
                  : access.cycleNumber > index,
              paidDueDate,
              current: true,
              start: index === 0,
              end: index === (access.installments ?? 1) - 1,
              overlapping: true,
            };
          });

          currentCycles.push(...(scheduledCycles as any));

          return [
            {
              id: index + (access.installments ?? 1),
              startAt: dayjs.unix(access.start_date),
              start: true,
              installment: 1,
              totalInstallments: 1,
              paid: true,
              paidDate: access.metadata?.prepaidDate,
              type: "recurring_overlapping",
              end: dayjs.unix(access.end_date),
            },
          ];
        }
        if (access.metadata?.prepaid) {
          return {
            id: index,
            startAt: dayjs.unix(access.start_date),
            start: true,
            installment: 1,
            totalInstallments: Number(access.metadata.totalIterations ?? 1),
            paid: true,
            current: false,
            end: false,
            paidDate: access.metadata?.prepaidDate || null,
          };
        }
        if (
          access.metadata?.extensionType === "installments_after_one_time_without_payment" &&
          access.metadata.iterations
        ) {
          const iterations = Number(access.metadata.iterations);
          return Array.from({ length: iterations }).map((_, i) => ({
            id: index + i,
            startAt: dayjs.unix(access.start_date).add(i, "month"),
            start: i === 0,
            installment: i + 1,
            totalInstallments: iterations,
            paid: false,
            current: false,
            end: i === iterations - 1,
            paidDate: null,
          }));
        } else if (access.metadata?.iterations) {
          const restIterations = Number(access.metadata.iterations);
          return Array.from({ length: restIterations }).map((_, i) => ({
            id: index + i,
            startAt: dayjs.unix(access.start_date).add(i, "month"),
            start: false,
            installment: i + 1 + 1,
            totalInstallments: restIterations + 1,
            paid: false,
            current: false,
            end: i === restIterations - 1,
            paidDate: null,
          }));
        }
        return null;
      })
      .filter((item) => !!item)
      .flat();

    return (
      <div className="px-0 py-4">
        <Divider orientation="left">Zaplanowane płatności</Divider>

        <div className="py-6">
          <Timeline
            mode="left"
            items={[
              ...currentCycles
                .sort((a, b) => a.startAt.diff(b.startAt))
                .map((cycle) => ({
                  label: cycle.overlapping
                    ? cycle.start
                      ? "Początek opłaty następnego cyklu"
                      : cycle.end
                      ? "Koniec opłaty następnego cyklu"
                      : undefined
                    : cycle.start
                    ? "Początek obecnego cyklu"
                    : cycle.end
                    ? "Koniec obecnego cyklu"
                    : undefined,
                  children: (
                    <span>
                      Rata {cycle.installment} / {cycle.totalInstallments} - płatność{" "}
                      {"paidDueDate" in cycle && typeof cycle.paidDueDate === "string"
                        ? dayjs(cycle.paidDueDate).format("DD.MM.YYYY")
                        : cycle.startAt.format("DD.MM.YYYY")}
                    </span>
                  ),
                  color: cycle.paid ? "green" : "#1677ff",
                })),
              {
                label: "Koniec obecnego cyklu",
                children: <span>{dayjs.unix(productClient.accessPeriod.currentPeriodEnd).format("DD.MM.YYYY")}</span>,
                color: "green",
              },
              ...scheduledAccessCycle
                .map((cycle) => {
                  if ("type" in cycle && cycle.type === "recurring_overlapping") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Raty opłacane w poprzednim cyklu - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  if ("type" in cycle && cycle.type === "one_time") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Płatność jednorazowa - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  if ("type" in cycle && cycle.type === "free_extension") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Dostęp przedłużony o darmowy okres - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  return {
                    label: cycle.start ? "Początek kolejnego cyklu" : cycle.end ? "Koniec cyklu" : undefined,
                    children: (
                      <span>
                        Rata {cycle.installment} / {cycle.totalInstallments} - płatność{" "}
                        {"paidDueDate" in cycle && typeof cycle.paidDueDate === "string"
                          ? dayjs(cycle.paidDueDate).format("DD.MM.YYYY")
                          : cycle.startAt.format("DD.MM.YYYY")}
                        {cycle.paid && cycle.paidDate
                          ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                          : ""}
                      </span>
                    ),
                    color: cycle.paid ? "green" : "#13c2c2",
                  };
                })
                .flat(),
            ]}
          />
        </div>
      </div>
    );
  }

  if (
    productClient.accessPeriod &&
    "installments" in productClient.accessPeriod &&
    productClient.accessPeriod.installments
  ) {
    // Destrukturyzacja prosto z `productClient.accessPeriod` — to ona niesie
    // zawężenie `installments` do liczby z warunku wyżej.
    const { installments, recurring, cycleNumber } = productClient.accessPeriod;
    const accessPeriod = productClient.accessPeriod;
    // Same rule the settings panel already applies: a paid-off plan is settled
    // even though its cycle counter stood still, so none of its installments is
    // a future payment (TRE-1725).
    const settled = areInstallmentsSettled(productClient.accessPeriod);

    const currentCycles = Array.from({
      length: productClient.accessPeriod.installments ?? 1,
    }).map((_, index) => ({
      startAt: installmentPaymentDate(accessPeriod, index + 1),
      id: index,
      installment: index + 1,
      totalInstallments: installments,
      paid: settled || cycleNumber > index,
      current: true,
      start: index === 0,
      end: index === installments - 1,
      overlapping: false,
    }));

    const scheduledAccessCycle = scheduledAccess
      .map((access, index) => {
        const cycleIndex = index;
        if ("type" in access && (access.type === "one_time" || access.type === "free_extension")) {
          return {
            id: index + installments,
            startAt: dayjs.unix(access.start_date),
            start: true,
            installment: 1,
            totalInstallments: 1,
            paid: true,
            paidDate: access.metadata?.prepaidDate,
            type: access.type,
            end: dayjs.unix(access.end_date),
          };
        }
        if (access.type === "recurring") {
          const scheduledCycles = Array.from({
            length: access.installments ?? 1,
          }).map((_, index) => {
            // @ts-expect-error ignore type error
            const paidDueDate = (scheduledAccess[cycleIndex]?.metadata?.updatedPaymentDate?.[index] || null) as
              | string
              | null;
            return {
              startAt: installmentPaymentDate(access, index + 1),
              id: index,
              installment: index + 1,
              paidDueDate,
              totalInstallments: access.installments,
              paid: areInstallmentsSettled(access)
                ? true
                : paidDueDate
                  ? dayjs(paidDueDate).isBefore(dayjs())
                  : access.cycleNumber > index,
              current: true,
              start: index === 0,
              end: index === (access.installments ?? 1) - 1,
              overlapping: true,
            };
          });

          currentCycles.push(...(scheduledCycles as any));

          return [
            {
              id: index + (access.installments ?? 1),
              startAt: dayjs.unix(access.start_date),
              start: true,
              installment: 1,
              totalInstallments: 1,
              paid: true,
              paidDate: access.metadata?.prepaidDate,
              type: "recurring_overlapping",
              end: dayjs.unix(access.end_date),
            },
          ];
        }
        if (access.metadata?.prepaid) {
          return {
            id: index + installments,
            startAt: dayjs.unix(access.start_date),
            start: true,
            installment: 1,
            totalInstallments: Number(access.metadata.totalIterations ?? 1),
            paid: true,
            current: false,
            end: false,
            paidDate: access.metadata?.prepaidDate || null,
          };
        }
        if (
          access.metadata?.extensionType === "installments_after_installments_without_payment" &&
          access.metadata.iterations
        ) {
          const iterations = Number(access.metadata.iterations);
          return Array.from({ length: iterations }).map((_, i) => ({
            id: index + i,
            startAt: dayjs.unix(access.start_date).add(i, recurring.interval),
            start: i === 0,
            installment: i + 1,
            totalInstallments: iterations,
            paid: false,
            current: false,
            end: i === iterations - 1,
            paidDate: null,
          }));
        } else if (access.metadata?.iterations) {
          const restIterations = Number(access.metadata.iterations);
          return Array.from({ length: restIterations }).map((_, i) => ({
            id: index + i,
            startAt: dayjs.unix(access.start_date).add(i, recurring.interval),
            start: false,
            installment: i + 1 + 1,
            totalInstallments: restIterations + 1,
            paid: false,
            current: false,
            end: i === restIterations - 1,
            paidDate: null,
          }));
        }
        return null;
      })
      .filter((item) => !!item)
      .flat();

    return (
      <div className="px-0 py-4">
        <Divider orientation="left">Zaplanowane płatności</Divider>

        <div className="py-6">
          <Timeline
            mode="left"
            items={[
              ...currentCycles
                .sort((a, b) => a.startAt.diff(b.startAt))
                .map((cycle) => ({
                  label: cycle.overlapping
                    ? cycle.start
                      ? "Początek opłaty następnego cyklu"
                      : cycle.end
                      ? "Koniec opłaty następnego cyklu"
                      : undefined
                    : cycle.start
                    ? "Początek obecnego cyklu"
                    : cycle.end
                    ? "Koniec obecnego cyklu"
                    : undefined,
                  children: (
                    <span>
                      Rata {cycle.installment} / {cycle.totalInstallments} - płatność{" "}
                      {"paidDueDate" in cycle && typeof cycle.paidDueDate === "string"
                        ? dayjs(cycle.paidDueDate).format("DD.MM.YYYY")
                        : cycle.startAt.format("DD.MM.YYYY")}
                    </span>
                  ),
                  color: cycle.paid ? "green" : "#1677ff",
                })),
              ...scheduledAccessCycle
                .map((cycle) => {
                  if ("type" in cycle && cycle.type === "recurring_overlapping") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Raty opłacane w poprzednim cyklu - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  if ("type" in cycle && cycle.type === "free_extension") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Dostęp przedłużony o darmowy okres - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  if ("type" in cycle && cycle.type === "one_time") {
                    return [
                      {
                        label: `Kolejny okres dostępu`,
                        children: (
                          <span>
                            Płatność jednorazowa - {cycle.startAt.format("DD.MM.YYYY")}
                            {cycle.paid && cycle.paidDate
                              ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                              : ""}
                          </span>
                        ),
                        color: cycle.paid ? "green" : "#13c2c2",
                      },
                      {
                        label: `Koniec dostępu`,
                        children: <span>{cycle.end.format("DD.MM.YYYY")}</span>,
                        color: "orange",
                      },
                    ];
                  }
                  return {
                    label: cycle.start ? "Początek kolejnego cyklu" : cycle.end ? "Koniec cyklu" : undefined,
                    children: (
                      <span>
                        Rata {cycle.installment} / {cycle.totalInstallments} - płatność{" "}
                        {"paidDueDate" in cycle && typeof cycle.paidDueDate === "string"
                          ? dayjs(cycle.paidDueDate).format("DD.MM.YYYY")
                          : cycle.startAt.format("DD.MM.YYYY")}
                        {cycle.paid && cycle.paidDate
                          ? ` (opłacone ${dayjs(cycle.paidDate).format("DD.MM.YYYY HH:mm")})`
                          : ""}
                      </span>
                    ),
                    color: cycle.paid ? "green" : "#13c2c2",
                  };
                })
                .flat(),
            ]}
          />
        </div>
      </div>
    );
  }

  return null;
};
